import { useEffect, useRef, useState, type FormEvent } from "react";
import { authConfigured, completeSignIn, currentUser, signIn, signOut } from "./auth";
import { ApiError, archiveOpportunity, createInvitation, loadAdminQueue, moderateInquiry, moderateInvitationRequest, moderateOpportunity } from "./api";
import type { AccessRequestContact, AdminUser, Inquiry, Invitation, InvitationRequest, OpportunitySubmission } from "./types";

function Brand() {
  return <span className="brand-word">B2B<span>Scouts</span></span>;
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function AuthCallbackPage() {
  const started = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    completeSignIn()
      .then(() => window.location.replace("/admin/"))
      .catch(() => setError(true));
  }, []);

  return (
    <main className="auth-callback">
      <Brand />
      <h1>{error ? "Anmeldung fehlgeschlagen" : "Anmeldung wird abgeschlossen…"}</h1>
      {error && <><p>Der Enterprise Login konnte nicht bestätigt werden.</p><a className="button button-primary" href="/admin/">Erneut versuchen</a></>}
    </main>
  );
}

export function AdminPage() {
  const [phase, setPhase] = useState<"loading" | "signed-out" | "ready" | "forbidden" | "error">("loading");
  const [user, setUser] = useState<AdminUser>();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationRequests, setInvitationRequests] = useState<InvitationRequest[]>([]);
  const [deliveryNotice, setDeliveryNotice] = useState<string>();
  const [submissions, setSubmissions] = useState<OpportunitySubmission[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [accessRequests, setAccessRequests] = useState<AccessRequestContact[]>([]);
  const [busyId, setBusyId] = useState<string>();
  const [actionError, setActionError] = useState<string>();

  useEffect(() => {
    if (!authConfigured) {
      setPhase("error");
      return;
    }
    currentUser()
      .then((signedInUser) => {
        if (!signedInUser) {
          setPhase("signed-out");
          return undefined;
        }
        return loadAdminQueue().then((queue) => {
          setUser(queue.user);
          setInvitations(queue.invitations);
          setInvitationRequests(queue.invitationRequests);
          setSubmissions(queue.submissions);
          setInquiries(queue.inquiries);
          setAccessRequests(queue.accessRequests);
          setPhase("ready");
        });
      })
      .catch((error: unknown) => {
        setPhase(error instanceof Error && error.message.includes("403") ? "forbidden" : "error");
      });
  }, []);

  async function issueInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyId("new-invitation");
    setActionError(undefined);
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const created = await createInvitation({
        company: String(form.get("company")),
        email: String(form.get("email")),
        validDays: Number(form.get("validDays")),
        locale: String(form.get("locale")) as "de" | "hu" | "en",
      });
      setDeliveryNotice(`Einladung an ${created.item.email} wurde per E-Mail versendet.`);
      setInvitations((items) => [created.item, ...items]);
      formElement.reset();
    } catch {
      setActionError("Die Einladung konnte nicht erstellt oder versendet werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function reviewInvitationRequest(id: string, decision: "approved" | "rejected") {
    setBusyId(id);
    setActionError(undefined);
    setDeliveryNotice(undefined);
    try {
      const updated = await moderateInvitationRequest(id, decision);
      setInvitationRequests((items) => items.map((item) => item.id === id ? updated : item));
      if (decision === "approved") {
        setDeliveryNotice(`Die Einladung an ${updated.email} wurde serverseitig versendet.`);
        const queue = await loadAdminQueue();
        setInvitations(queue.invitations);
      }
    } catch {
      setActionError(decision === "approved" ? "Die Anfrage konnte nicht freigegeben oder die Einladung nicht versendet werden." : "Die Anfrage konnte nicht abgelehnt werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function reviewSubmission(id: string, decision: "approved" | "rejected") {
    setBusyId(id);
    setActionError(undefined);
    try {
      const updated = await moderateOpportunity(id, decision);
      setSubmissions((items) => items.map((item) => item.id === id ? updated : item));
    } catch {
      setActionError("Die Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function archivePublishedOpportunity(id: string) {
    setBusyId(id);
    setActionError(undefined);
    try {
      await archiveOpportunity(id);
      setSubmissions((items) => items.map((item) => item.id === id ? { ...item, publicationStatus: "archived" } : item));
    } catch {
      setActionError("Der veröffentlichte Eintrag konnte nicht archiviert werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function reviewInquiry(id: string, decision: "handled" | "rejected") {
    setBusyId(id);
    setActionError(undefined);
    try {
      const updated = await moderateInquiry(id, decision);
      setInquiries((items) => items.map((item) => item.id === id ? updated : item));
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const queue = await loadAdminQueue().catch(() => undefined);
        if (queue) setInquiries(queue.inquiries);
        setActionError("Diese Kontaktanfrage wurde bereits abschließend bearbeitet.");
      } else {
        setActionError("Die Entscheidung konnte nicht gespeichert werden.");
      }
    } finally {
      setBusyId(undefined);
    }
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <a href="/" aria-label="Zur Plattform"><Brand /></a>
        <div><span>B2B Scouts Administration</span>{phase === "ready" && <button type="button" onClick={() => void signOut()}>Abmelden</button>}</div>
      </header>

      {phase === "loading" && <main className="admin-state"><h1>Administration wird geladen…</h1></main>}
      {phase === "signed-out" && <main className="admin-state"><p className="eyebrow"><span />Geschützter Bereich</p><h1>Administration</h1><p>Melden Sie sich mit Ihrem Unternehmenskonto an, um Einträge und Anfragen zu moderieren.</p><button className="button button-primary" type="button" onClick={() => void signIn()}>Enterprise Login <span>↗</span></button></main>}
      {phase === "forbidden" && <main className="admin-state"><h1>Kein Zugriff</h1><p>Ihr Unternehmenskonto hat nicht die erforderliche Rolle <code>broker-admin</code>.</p><button className="button button-primary" type="button" onClick={() => void signOut()}>Abmelden <span>↗</span></button></main>}
      {phase === "error" && <main className="admin-state"><h1>Administration nicht verfügbar</h1><p>Die Anmeldung oder die API-Konfiguration konnte nicht geladen werden.</p><a className="button button-primary" href="/admin/">Erneut versuchen <span>↻</span></a></main>}

      {phase === "ready" && <main className="admin-main">
        <div className="admin-title"><div><p className="eyebrow"><span />Moderation</p><h1>Broker Administration</h1></div><p>Angemeldet als <strong>{user?.name ?? user?.email ?? user?.sub}</strong></p></div>
        {actionError && <p className="admin-error" role="alert">{actionError}</p>}

        <section className="admin-section">
          <div className="admin-section-title"><h2>Zugangsanfragen</h2><span>{accessRequests.length} Kontakte · {accessRequests.reduce((total, item) => total + item.requestCount, 0)} Anfragen</span></div>
          <div className="admin-grid">
            {accessRequests.length === 0 && <p className="admin-empty">Keine Zugangsanfragen vorhanden.</p>}
            {accessRequests.map((item) => <article className="admin-card" key={item.id}>
              <div className="admin-card-head"><span className="admin-status admin-status--pending">Zugang anfragen</span><time>{dateTime(item.lastRequestedAt)}</time></div>
              <p className="admin-kicker">Coming-soon · {item.locale.toUpperCase()}</p>
              <h3><a className="admin-access-email" href={`mailto:${item.email}`}>{item.email}</a></h3>
              <dl><div><dt>Erste Anfrage</dt><dd>{dateTime(item.firstRequestedAt)}</dd></div><div><dt>Anfragen</dt><dd>{item.requestCount}</dd></div></dl>
            </article>)}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-title"><h2>Einladungsanfragen</h2><span>{invitationRequests.filter((item) => item.status === "pending").length} offen</span></div>
          <div className="admin-grid">
            {invitationRequests.length === 0 && <p className="admin-empty">Keine Einladungsanfragen vorhanden.</p>}
            {invitationRequests.map((item) => <article className="admin-card" key={item.id}>
              <div className="admin-card-head"><span className={`admin-status admin-status--${item.status}`}>{item.status}</span><time>{dateTime(item.submittedAt)}</time></div>
              <p className="admin-kicker">Einladungsanfrage</p><h3>{item.company}</h3><p>{item.reason}</p>
              <div className="admin-contact"><strong>{item.contactName}</strong><a href={`mailto:${item.email}`}>{item.email}</a>{item.website && <a href={item.website} target="_blank" rel="noreferrer">Website ↗</a>}{item.referralCode && <span>Empfehlung: {item.referralCode}</span>}</div>
              {item.status === "pending" && <div className="admin-actions"><button className="approve" disabled={busyId === item.id} onClick={() => void reviewInvitationRequest(item.id, "approved")}>Freigeben & senden</button><button className="reject" disabled={busyId === item.id} onClick={() => void reviewInvitationRequest(item.id, "rejected")}>Ablehnen</button></div>}
            </article>)}
          </div>
        </section>

        <section className="admin-section admin-invitations">
          <div className="admin-section-title"><h2>Direkte Einladungen</h2><span>Versand über B2B Scouts Mail</span></div>
          <form className="admin-invite-form" onSubmit={issueInvitation}>
            <label>Unternehmen<input name="company" required minLength={2} maxLength={120} /></label>
            <label>Geschäftliche E-Mail<input name="email" type="email" required maxLength={200} /></label>
            <label>Sprache<select name="locale" defaultValue="de"><option value="de">Deutsch</option><option value="hu">Magyar</option><option value="en">English</option></select></label>
            <label>Gültigkeit<select name="validDays" defaultValue="14"><option value="7">7 Tage</option><option value="14">14 Tage</option><option value="30">30 Tage</option></select></label>
            <button type="submit" disabled={busyId === "new-invitation"}>Einladung senden</button>
          </form>
          {deliveryNotice && <div className="admin-invite-link"><p>{deliveryNotice}</p></div>}
          <div className="admin-invite-list">
            {invitations.length === 0 && <p className="admin-empty">Noch keine Einladungen vorhanden.</p>}
            {invitations.map((item) => {
              const status = item.usedAt ? "verwendet" : item.expiresAt < new Date().toISOString() ? "abgelaufen" : item.sentAt ? "versendet" : "offen";
              return <article key={item.id}><div><strong>{item.company}</strong><a href={`mailto:${item.email}`}>{item.email}</a>{item.referralCode && <button className="admin-copy-referral" type="button" onClick={() => void navigator.clipboard.writeText(`${window.location.origin}/?ref=${encodeURIComponent(item.referralCode!)}`)}>Empfehlungslink kopieren</button>}</div><span className={`admin-status admin-status--${item.usedAt ? "handled" : status === "versendet" ? "approved" : status === "offen" ? "pending" : "rejected"}`}>{status}</span><time>bis {dateTime(item.expiresAt)}</time></article>;
            })}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-title"><h2>Einträge</h2><span>{submissions.filter((item) => item.status === "pending").length} offen</span></div>
          <div className="admin-grid">
            {submissions.length === 0 && <p className="admin-empty">Keine Einreichungen vorhanden.</p>}
            {submissions.map((item) => {
              const displayStatus = item.publicationStatus === "archived" ? "archived" : item.status;
              return <article className="admin-card" key={item.id}>
                <div className="admin-card-head"><span className={`admin-status admin-status--${displayStatus}`}>{displayStatus}</span><time>{dateTime(item.submittedAt)}</time></div>
                <p className="admin-kicker">{item.kind === "contract" ? "Auftrag" : "Kapazität"} · {item.city}, {item.country}</p>
                <h3>{item.title}</h3><p>{item.summary}</p>
                <dl><div><dt>Unternehmen</dt><dd>{item.company}</dd></div><div><dt>Branche</dt><dd>{item.sector}</dd></div><div><dt>Umfang</dt><dd>{item.volume}</dd></div><div><dt>Sprache</dt><dd>{item.locale.toUpperCase()}</dd></div></dl>
                <div className="admin-contact"><strong>{item.contactName}</strong><a href={`mailto:${item.contactEmail}`}>{item.contactEmail}</a>{item.website && <a href={item.website} target="_blank" rel="noreferrer">Website ↗</a>}</div>
                {item.status === "pending" && <div className="admin-actions"><button className="approve" disabled={busyId === item.id} onClick={() => void reviewSubmission(item.id, "approved")}>Veröffentlichen</button><button className="reject" disabled={busyId === item.id} onClick={() => void reviewSubmission(item.id, "rejected")}>Ablehnen</button></div>}
                {item.status === "approved" && item.publicationStatus !== "archived" && <div className="admin-actions"><button className="reject" disabled={busyId === item.id} onClick={() => void archivePublishedOpportunity(item.id)}>Veröffentlichung beenden</button></div>}
              </article>;
            })}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-title"><h2>Kontaktanfragen</h2><span>{inquiries.filter((item) => item.status === "pending").length} offen</span></div>
          <div className="admin-grid">
            {inquiries.length === 0 && <p className="admin-empty">Keine Kontaktanfragen vorhanden.</p>}
            {inquiries.map((item) => <article className="admin-card" key={item.id}>
              <div className="admin-card-head"><span className={`admin-status admin-status--${item.status}`}>{item.status}</span><time>{dateTime(item.submittedAt)}</time></div>
              <p className="admin-kicker">Eintrag {item.opportunityId}</p><h3>{item.company}</h3><p>{item.message}</p>
              <div className="admin-contact"><strong>{item.name}</strong><a href={`mailto:${item.email}`}>{item.email}</a></div>
              {item.status === "pending" && <div className="admin-actions"><button className="approve" disabled={busyId === item.id} onClick={() => void reviewInquiry(item.id, "handled")}>Als bearbeitet markieren</button><button className="reject" disabled={busyId === item.id} onClick={() => void reviewInquiry(item.id, "rejected")}>Ablehnen</button></div>}
            </article>)}
          </div>
        </section>
      </main>}
    </div>
  );
}
